import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Subscription } from 'rxjs';
import { Observable } from 'rxjs/Observable';
import { debounceTime, map } from 'rxjs/operators';
import { BaseInputComponent } from '../base-input/base-input.component';
import { ChipListInputType } from '../form.types';
import { QuestionStateService } from '../question-state.service';

@Component({
    selector: 'app-chip-list-select',
    templateUrl: './chip-list-select.component.html',
    styleUrls: ['./chip-list-select.component.scss'],
    encapsulation: ViewEncapsulation.Emulated
})
export class ChipListSelectComponent extends BaseInputComponent implements OnInit, OnDestroy {
    @Input() input: ChipListInputType;
    options: Array<any>;
    chips: Array<any> = [];
    searchControl: UntypedFormControl = new UntypedFormControl('');
    @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
    @ViewChild('auto') autoComplete: MatAutocomplete;
    private subscription$: Subscription;
    loading: boolean;
    errorLoading: boolean;
    noSearchResultsFound: boolean;
    private pipeSubscription$: Subscription;

    constructor(private http: HttpClient, private questionStateService: QuestionStateService) {
        super();
    }

    ngOnInit(): void {
        this.initialize(this.input, this.questionStateService);
        this.subscribe();
        this.getInitialValues();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.subscription$) {
            this.subscription$.unsubscribe();
        }
        if (this.pipeSubscription$) {
            this.pipeSubscription$.unsubscribe();
        }
    }
    getInitialValues() {
        if (this.input.baseUrl && this.input.control.value) {
            const values: Array<string> = this.input.control.value as Array<string>;
            values.forEach((v) => {
                this.loading = true;
                this.http
                    .get<any>(`/api/${this.input.baseUrl}?${this.input.queryParam}=${v}&initialValue=true`)
                    .subscribe({
                        next: (data) => {
                            if (data && Array.isArray(data)) {
                                data.forEach((d) => {
                                    this.chips.push(d);
                                });
                            } else {
                                this.chips.push(data);
                            }
                            this.loading = false;
                        },
                        error: (error) => {
                            this.errorLoading = true;
                            this.loading = false;
                        }
                    });
            });
        }
    }

    subscribe() {
        if (this.input.baseUrl) {
            this.input.queryParam = this.input.queryParam ? this.input.queryParam : 'q';
            this.pipeSubscription$ = this.searchControl.valueChanges
                .pipe(
                    debounceTime(300),
                    map((value) => {
                        if (value && value.length > 2) {
                            this.loading = true;
                            return value;
                        }
                    })
                )
                .subscribe((value) => {
                    if (!value) {
                        return;
                    }
                    if (this.subscription$) {
                        this.subscription$.unsubscribe();
                    }
                    this.subscription$ = this.http
                        .get<Array<any>>(`/api/${this.input.baseUrl}?${this.input.queryParam}=${value}`)
                        .subscribe({
                            next: (data) => {
                                this.options = data;
                                this.noSearchResultsFound = false;
                                if (!data || data.length === 0) {
                                    this.noSearchResultsFound = true;
                                }
                                this.loading = false;
                            },
                            error: () => {
                                this.loading = false;
                                this.errorLoading = true;
                            }
                        });
                });
        }
    }

    selected($event: MatAutocompleteSelectedEvent) {
        this.chips.push($event.option.value);
        this.searchInput.nativeElement.value = '';
        this.searchControl.setValue(null);
        let value: Array<string> = this.input.control.value as Array<string>;
        value = value ?? [];
        if (value.indexOf($event.option.value[this.input.property]) === -1) {
            value.push($event.option.value[this.input.property]);
            this.input.control.setValue(value);
        }
    }

    remove(v: any) {
        if (this.chips.length > 0) {
            const index = this.chips.map((x) => x[this.input.property]).indexOf(v);
            if (index >= 0) {
                this.chips.splice(index, 1);
            }
        }
        let value: Array<string> = this.input.control.value as Array<string>;
        value = value ?? [];
        const valueIndex = value.indexOf(v);
        if (valueIndex >= 0) {
            value.splice(valueIndex, 1);
            this.input.control.setValue(value);
        }
    }
}
